/**
 * Realtime transport adapter (WP-3.3 — Realtime → SSE).
 *
 * ONE module that all 5 realtime surfaces subscribe through, with two
 * implementations selected at BUILD time by `REACT_APP_REALTIME_TRANSPORT`:
 *
 *   - 'supabase' (DEFAULT / unset): byte-identical to the pre-WP-3.3 code — each
 *     surface opens the SAME supabase.channel(...) it always did (postgres_changes
 *     with the same filters, or the same broadcast topic/event). R2: nothing about
 *     the Supabase path changes.
 *   - 'sse': a SINGLE EventSource (per site) multiplexes every channel; the server
 *     (GET /api/events) fans out published events. The JWT rides as a query param
 *     (EventSource can't set headers) and is refreshed-and-reconnected on auth
 *     error; exponential backoff on transient drops.
 *
 * The flip is a build-time env change + a static-site redeploy on Render (the
 * frontend is a static bundle — see docs/migration-evidence/wp-3-3-realtime-sse.md).
 *
 * Each surface gets a typed subscribe function so the 5 consumers keep their exact
 * handler contracts; the adapter reshapes an SSE frame back into the same shape the
 * Supabase callback delivered. Every subscribe returns an unsubscribe function.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { getCurrentSiteId } from '../services/api';

/** Build-time transport selector. Unset/anything-but-'sse' => Supabase (R2 default). */
export const REALTIME_TRANSPORT: 'supabase' | 'sse' =
  process.env.REACT_APP_REALTIME_TRANSPORT === 'sse' ? 'sse' : 'supabase';

export function isSse(): boolean {
  return REALTIME_TRANSPORT === 'sse';
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ===========================================================================
// Shapes the surfaces consume (mirrors the Supabase payloads they read today).
// ===========================================================================

/** Supabase postgres_changes-style payload the Dashboard + NotificationBell read. */
export interface ChangePayload<T = any> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE' | 'BULK';
  new: T | null;
  old?: T | null;
}

/** Broadcast payload the InboundSmsToastHost reads (ids only — no PHI). */
export interface InboundSmsPayload {
  contactId?: string;
  ownerUserId?: string | null;
}

/** Broadcast payload the CrmAlertHost reads (ids only — no PHI). The host
 *  re-fetches /api/crm/alerts/active for the display data on every ping.
 *  `kind` was added with the clinician portal's referral_consented alerts; the
 *  two kinds share this one topic deliberately (channel authorization is a
 *  fail-closed literal-string switch, and the host re-fetches everything on any
 *  ping regardless). Older senders omit it — treat a missing kind as
 *  'lead_call'. */
export interface LeadAlertPayload {
  alertId?: string;
  contactId?: string | null;
  assignedUserId?: string | null;
  phase?: 'assigned' | 'escalated' | 'claimed' | null;
  kind?: string | null;
}

// ===========================================================================
// SSE multiplexer (only constructed in 'sse' mode). One EventSource per distinct
// channel set; channels are added by (re)opening with the union of active
// channels. A per-channel listener registry dispatches server frames.
// ===========================================================================

type Frame = { channel: string; event: string; payload: any };
type FrameListener = (frame: Frame) => void;

// Per-tab connection id (owner-actions §39). Stable for the life of the tab
// (sessionStorage: a reload keeps it, a new tab gets its own) and sent as ?cid= so
// the server can replace THIS tab's previous stream wherever it landed instead of
// leaving it to run out the Cloud Run request timeout while holding a request slot.
// Rotated on a collision (a duplicated tab copies sessionStorage, so two live tabs
// can share one id until the server tells one of them it was displaced).
const CID_STORAGE_KEY = 'siteops.sse.cid';
const CID_RE = /^[A-Za-z0-9_-]{8,64}$/;

function randomId(): string {
  const c = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID().replace(/-/g, '');
  let out = '';
  for (let i = 0; i < 32; i++) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

function rotateTabConnectionId(): string {
  const id = randomId();
  try {
    window.sessionStorage.setItem(CID_STORAGE_KEY, id);
  } catch {
    /* storage unavailable — a per-load id still dedupes this tab's reopens */
  }
  return id;
}

function loadTabConnectionId(): string {
  try {
    const stored = window.sessionStorage.getItem(CID_STORAGE_KEY);
    if (stored && CID_RE.test(stored)) return stored;
  } catch {
    /* noop */
  }
  return rotateTabConnectionId();
}

class SseMultiplexer {
  private es: EventSource | null = null;
  private listeners = new Map<string, Set<FrameListener>>();
  private currentSite: string | null = null;
  private backoff = 1000;
  private readonly maxBackoff = 30_000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private opening = false;
  /** A reopen was requested while one was in flight — re-evaluate when it finishes. */
  private dirty = false;
  /** What the live EventSource was opened with, so a subscribe that adds nothing new
   *  (or any unsubscribe) never tears down a healthy stream. Every teardown used to
   *  leave a server-side zombie for up to an hour (§39), so reopens are now the
   *  exception: only a NEW channel, a site change, or a server `bye` triggers one. */
  private connected: { channels: Set<string>; site: string | null } | null = null;
  private cid = loadTabConnectionId();

  /** Subscribe a listener to a channel. Returns unsubscribe. */
  subscribe(channel: string, siteId: string | null, listener: FrameListener): () => void {
    if (!this.listeners.has(channel)) this.listeners.set(channel, new Set());
    this.listeners.get(channel)!.add(listener);
    this.currentSite = siteId ?? this.currentSite;
    // Reopens only if the live connection lacks this channel (see needsReopen).
    void this.reopen();

    return () => {
      const set = this.listeners.get(channel);
      if (set) {
        set.delete(listener);
        if (set.size === 0) this.listeners.delete(channel);
      }
      // Shrinking never reopens: the server keeps pushing the dropped channel and
      // dispatch() discards it; the next reopen (a new channel, or the server's
      // max-age `bye`) prunes the set. Only an empty set closes the stream.
      if (this.listeners.size === 0) this.close();
    };
  }

  private channelList(): string[] {
    return Array.from(this.listeners.keys());
  }

  private close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.connected = null;
  }

  private needsReopen(channels: string[], site: string | null): boolean {
    if (!this.es || !this.connected) return true;
    if ((this.es as any).readyState === 2 /* EventSource.CLOSED */) return true;
    if (this.connected.site !== site) return true;
    return channels.some((c) => !this.connected!.channels.has(c));
  }

  /** Open (or reopen) the EventSource with the current channel union + a fresh token —
   *  but only when the live connection cannot already serve that union. */
  private async reopen(): Promise<void> {
    if (this.opening) {
      this.dirty = true;
      return;
    }
    this.opening = true;
    try {
      do {
        this.dirty = false;
        if (this.listeners.size === 0) {
          this.close();
          return;
        }
        // Fresh token each (re)connect so a rotated session reconnects cleanly.
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          // No session — retry with backoff (the user may still be signing in).
          this.scheduleReconnect();
          return;
        }
        // Read the union AFTER the await so every subscribe that landed in the same
        // tick (a page mount) rides ONE connection instead of one per surface.
        const channels = this.channelList();
        // The CRM subscribers (crm:inbound-sms, crm:contact:*) subscribe with a
        // null siteId, so `currentSite` is only set when the Dashboard's
        // appointments subscriber is also mounted. On CRM-only pages that leaves
        // `site` unset and the backend's CRM channel auth fail-closes (403). Fall
        // back to the persisted site (same `currentSiteId` the REST X-Site-Id
        // header uses) so CRM realtime works on every page.
        const site = this.currentSite ?? getCurrentSiteId();
        if (!this.needsReopen(channels, site)) continue;
        this.close();

        const params = new URLSearchParams();
        params.set('token', token);
        if (site) params.set('site', site);
        params.set('channels', channels.join(','));
        params.set('cid', this.cid);

        const es = new EventSource(`${API_URL}/api/events?${params.toString()}`);
        this.es = es;
        this.connected = { channels: new Set(channels), site };

        es.addEventListener('open', () => {
          this.backoff = 1000; // reset backoff on a good connection
        });

        es.addEventListener('message', (ev: MessageEvent) => {
          this.dispatch(ev.data);
        });

        // The server also emits a named "ready" event listing granted channels; we
        // don't need to act on it, but listening avoids it surfacing as an error.
        es.addEventListener('ready', () => {});

        // The server ends every stream on purpose (max age, replaced, evicted,
        // user cap) and says why before closing — see routes/events.js.
        es.addEventListener('bye', (ev: Event) => this.onBye(es, ev as MessageEvent));

        es.addEventListener('error', () => {
          // A source we already replaced can still fire; ignore it.
          if (this.es !== es) return;
          // EventSource auto-reconnects on network blips, but on an auth failure the
          // stream just errors repeatedly. Close and reconnect ourselves with a
          // fresh token after backoff (covers expired-JWT + transient drops).
          this.close();
          this.scheduleReconnect();
        });
      } while (this.dirty);
    } finally {
      this.opening = false;
    }
  }

  private onBye(es: EventSource, ev: MessageEvent) {
    if (this.es !== es) return;
    let reason = '';
    try {
      reason = String(JSON.parse(ev.data)?.reason ?? '');
    } catch {
      /* noop */
    }
    this.close();
    if (reason === 'replaced' || reason === 'evicted') {
      // We are alive and still got displaced: another tab shares our cid (a
      // duplicated tab copies sessionStorage). Rotate so the two stop evicting
      // each other, then reconnect.
      this.cid = rotateTabConnectionId();
    }
    if (reason === 'user_cap') {
      // More tabs than the server allows per user — don't fight the cap.
      this.backoff = this.maxBackoff;
      this.scheduleReconnect();
      return;
    }
    void this.reopen();
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, this.maxBackoff);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.reopen();
    }, delay);
  }

  private dispatch(raw: string) {
    let frame: Frame;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }
    const set = this.listeners.get(frame.channel);
    if (!set) return;
    for (const l of set) {
      try {
        l(frame);
      } catch {
        /* a listener throwing must not kill the stream */
      }
    }
  }
}

// Lazily constructed so 'supabase' builds never instantiate an EventSource.
let mux: SseMultiplexer | null = null;
function multiplexer(): SseMultiplexer {
  if (!mux) mux = new SseMultiplexer();
  return mux;
}

// ===========================================================================
// Per-surface subscribe helpers. Each preserves the exact handler contract the
// corresponding consumer had against Supabase.
// ===========================================================================

/**
 * Dashboard — appointment changes for a site. `onChange` receives the same
 * postgres_changes-style payload it read before ({ eventType, new, old }).
 */
export function subscribeAppointments(
  siteId: string,
  onChange: (payload: ChangePayload) => void,
): () => void {
  if (isSse()) {
    const unsub = multiplexer().subscribe(`appointments:${siteId}`, siteId, (frame) => {
      onChange(frame.payload as ChangePayload);
    });
    return unsub;
  }
  // Supabase path — verbatim the Dashboard's original subscription.
  const channel: RealtimeChannel = supabase
    .channel(`appointments-${siteId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'appointments', filter: `site_id=eq.${siteId}` },
      (payload: any) => onChange(payload as ChangePayload),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * IXVerify Notifications PAGE — refetch on ANY notification change for the user.
 * `onChange` takes no argument (the page just refetches), matching the original.
 */
export function subscribeIxverifyNotificationsAll(
  userId: string,
  onChange: () => void,
): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`ixverify:${userId}`, null, () => onChange());
  }
  const channel = supabase
    .channel('notifications-page')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      () => onChange(),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * IXVerify NotificationBell — INSERT and UPDATE for the user, each delivering the
 * row (`payload.new`). Matches the bell's two-handler original. The
 * `ixverify:{userId}` channel also carries `ixv_session_update` frames (ixv2
 * WP-3.1) whose payload has the same {eventType, new} shape, so the SSE branch
 * filters by frame event — only 'ixverify_notification' frames reach the bell
 * (three-file rule with backend realtimeEmit.emitIxverifyNotification).
 */
export function subscribeIxverifyNotificationsBell<T = any>(
  userId: string,
  onInsert: (row: T) => void,
  onUpdate: (row: T) => void,
): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`ixverify:${userId}`, null, (frame) => {
      if (frame.event !== 'ixverify_notification') return;
      const p = frame.payload as ChangePayload<T>;
      if (p.eventType === 'INSERT' && p.new) onInsert(p.new);
      else if (p.eventType === 'UPDATE' && p.new) onUpdate(p.new);
    });
  }
  const channel = supabase
    .channel('ixverify_notifications')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      (payload: any) => onInsert(payload.new as T),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      (payload: any) => onUpdate(payload.new as T),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * CRM CommsThread — content-free "changed" ping on a contact's topic. `onPing`
 * takes no argument (the thread re-fetches through the authed API), matching the
 * original broadcast handler.
 */
export function subscribeContactChanges(contactId: string, onPing: () => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`crm:contact:${contactId}`, null, () => onPing());
  }
  const channel = supabase
    .channel(`crm:contact:${contactId}`)
    .on('broadcast', { event: 'changed' }, () => onPing())
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * CRM InboundSmsToastHost — the fixed network-wide inbound-SMS topic. `onSms`
 * receives the ids-only payload ({ contactId, ownerUserId }), matching the
 * original broadcast handler's `msg.payload`.
 */
export function subscribeInboundSms(onSms: (payload: InboundSmsPayload) => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe('crm:inbound-sms', null, (frame) => {
      onSms((frame.payload || {}) as InboundSmsPayload);
    });
  }
  const channel = supabase
    .channel('crm:inbound-sms')
    .on('broadcast', { event: 'sms_inbound' }, (msg: any) => {
      onSms((msg?.payload || {}) as InboundSmsPayload);
    })
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * CRM LeadCallAlertHost — the fixed network-wide hot-lead call-alert topic. `onPing`
 * receives the ids-only payload; the host re-fetches the authed active list to
 * reconcile its pop-ups (mirrors subscribeInboundSms exactly, on a fixed topic).
 */
export function subscribeLeadAlerts(onPing: (payload: LeadAlertPayload) => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe('crm:lead-alert', null, (frame) => {
      onPing((frame.payload || {}) as LeadAlertPayload);
    });
  }
  const channel = supabase
    .channel('crm:lead-alert')
    .on('broadcast', { event: 'lead_alert' }, (msg: any) => {
      onPing((msg?.payload || {}) as LeadAlertPayload);
    })
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * IXVerify v2 session updates (ixv2 WP-3.1/WP-3.2) — the SAME authorized
 * `ixverify:{userId}` channel, event name EXACTLY 'ixv_session_update'
 * (three-file rule with backend realtimeEmit.emitIxvSessionUpdate). The
 * payload is the PHI-free session summary ({ eventType:'UPDATE', new: row }).
 * The notification events on this channel carry a different event name, so we
 * filter by frame.event here; in Supabase mode (local dev) there is no v2
 * emitter — the dashboard polls/refetches instead, so this is a no-op there.
 */
export function subscribeIxvSessionUpdates<T = any>(
  userId: string,
  onUpdate: (session: T) => void,
): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`ixverify:${userId}`, null, (frame) => {
      if (frame.event !== 'ixv_session_update') return;
      const p = frame.payload as ChangePayload<T>;
      if (p?.new) onUpdate(p.new);
    });
  }
  // Local dev (frozen snapshot) has no v2 session emitter — nothing to hear.
  return () => {};
}

/** Ids-only payload on the fixed texting-agent topic (texting-agent plan §7.4). */
export interface AgentEventPayload {
  kind: string;
  conversationId?: string | null;
  escalationId?: string | null;
  turnId?: string | null;
  contactId?: string | null;
}

/**
 * CRM texting-agent console/popup — the fixed network-wide `crm:agent` topic.
 * `onEvent` receives the ids-only payload; subscribers re-fetch their authed
 * lists (board, draft queue, escalations) on any ping. Mirrors
 * subscribeLeadAlerts exactly, on its own fixed topic.
 */
export function subscribeAgentEvents(onEvent: (payload: AgentEventPayload) => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe('crm:agent', null, (frame) => {
      onEvent((frame.payload || {}) as AgentEventPayload);
    });
  }
  const channel = supabase
    .channel('crm:agent')
    .on('broadcast', { event: 'agent_event' }, (msg: any) => {
      onEvent((msg?.payload || {}) as AgentEventPayload);
    })
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

export default {
  REALTIME_TRANSPORT,
  isSse,
  subscribeAppointments,
  subscribeIxverifyNotificationsAll,
  subscribeIxverifyNotificationsBell,
  subscribeIxvSessionUpdates,
  subscribeContactChanges,
  subscribeInboundSms,
  subscribeLeadAlerts,
  subscribeAgentEvents,
};
